import React, { useContext, useEffect, useState } from 'react';
import { ClaimComments } from '../CommonTypes';
import ErrorSummary from '../ErrorSummary/ErrorSummary';
import DocumentUploader from '../fileUpload/DocumentUploader';
import FormInput from '../FormInput';
import PageTitle from '../PageTitle';
import TextArea from '../textArea/TextArea';
import { ClaimContext } from 'context/ClaimContext';
import { useAuth0 } from '@auth0/auth0-react';
import { ClaimApi } from '../../../api/ClaimApi';
import { toast } from 'react-toastify';
import { RoutePaths } from '../../../Constants/Common';
import {
    DocumentStatus as Status,
    GapClaimDocumentStatusText as StatusText
} from '../../../components/common/policyholderinfo/PolicyHolderInfoTypes';
import { useNavigate } from 'react-router-dom';
import { useClaimDocumentsUpload } from '../../hooks/useClaimDocumentsUpload';
import { useTranslation } from 'react-i18next';

const ClaimDocuments = (): JSX.Element => {
    const [errorMessage, setErrorMessage] = useState('');
    const { t } = useTranslation();
    const { gapClaim, claimRequest } = useContext(ClaimContext);
    const { onDocumentUploaded, setUploadedDocuments, uploadedDocuments } =
        useClaimDocumentsUpload(claimRequest);

    const { getAccessTokenSilently } = useAuth0();
    const [claimComments, setClaimComments] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        if (claimComments.length > 500) {
            setErrorMessage(
                t('comments_should_not_be_greater_than_500_characters')
            );
        } else {
            setErrorMessage('');
        }
    }, [claimComments]);

    useEffect(() => {
        const initialUploadedDocuments = [];
        gapClaim?.requiredDocuments?.forEach(() =>
            initialUploadedDocuments.push(undefined)
        );
        setUploadedDocuments(initialUploadedDocuments);
    }, []);

    const onChangeEvent = (comments: string) => {
        setClaimComments(comments);
    };

    const saveClaimComments = async () => {
        const successCallback = (response) => {
            if (response > 0) setClaimComments('');
            toast.success(t('submitted'), { theme: 'colored' });
        };

        const failureCallback = () => {
            toast.error(t('comments_submit_toast_errormsg'));
        };

        const userComments: ClaimComments = {
            claimId: +claimRequest.claimRequestId,
            comments: claimComments
        };

        const token = await getAccessTokenSilently();

        new ClaimApi().saveClaimComments(
            userComments,
            token,
            successCallback,
            failureCallback,
            { skipToastNotification: true }
        );
    };
    const onAddCommentsClick = async () => {
        if (claimComments.length > 0 && claimComments.length <= 500)
            await saveClaimComments();
    };

    const onBackButtonClick = (): void => {
        navigate(RoutePaths.Home);
    };

    return (
        <div>
            <div className='card page-content'>
                <PageTitle title={t('upload_documents')} />
            </div>
            <div className='card-body page-content-body' id='documents-section'>
                <FormInput id='document-section' label='Documents'>
                    <ul className='list-group list-group-flush'>
                        {gapClaim?.requiredDocuments?.map((document, index) => {
                            return (
                                <DocumentUploader
                                    id={`document-${index}`}
                                    key={`document-${index}`}
                                    documentIndex={index}
                                    documentName={document.documentName}
                                    allowUpload={!uploadedDocuments[index]}
                                    uploadedDocument={uploadedDocuments[index]}
                                    status={
                                        uploadedDocuments[index]
                                            ? StatusText[Status.UploadedPending]
                                            : StatusText[Status.NotUploaded]
                                    }
                                    helpText={document.helpText}
                                    onDocumentUploaded={onDocumentUploaded}
                                />
                            );
                        })}
                    </ul>
                </FormInput>

                <FormInput id='comments-forminput' label={t('comments')}>
                    <TextArea
                        name='commentsContent'
                        rows={4}
                        onChange={(e: React.FormEvent<HTMLTextAreaElement>) =>
                            onChangeEvent(e.currentTarget.value)
                        }
                        innerClassName='fullscreen'></TextArea>
                    <div className='bottom-navigation no-border'>
                        <button
                            id='btnAddComments'
                            className='btn btn-primary'
                            onClick={onAddCommentsClick}>
                            {t('add_comments')}
                        </button>
                    </div>
                </FormInput>
            </div>
            <div className='card-footer page-content-footer'>
                {errorMessage && (
                    <ErrorSummary
                        title=''
                        errors={[errorMessage]}
                        className='documents-error'
                    />
                )}
            </div>
            <div className='bottom-navigation'>
                <button
                    aria-label='back-button'
                    className='btn btn-secondary progress-button'
                    onClick={() => onBackButtonClick()}>
                    {t('back')}
                </button>
            </div>
        </div>
    );
};

export default ClaimDocuments;
